import { useIsVip } from "@/hooks/use-user-roles";
import type { PublicationMode } from "@/lib/publicacion-mode";
import { useEffect, useRef, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AvatarUploader } from "@/components/common/AvatarUploader";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Phone, Mail, Plus, Megaphone, Calendar, MessageSquare, UserCircle, LifeBuoy, Target, Crown } from "lucide-react";
import { AuthDialog } from "@/components/site/AuthDialog";
import {
  getMyBizacliaResumen,
  listMisSolicitudes,
  listMisActividades,
  listMisAnuncios,
  listMisDemandas,
  updateMiCuenta,
} from "@/lib/mybizaclia.functions";
import { PublicarAnuncioWizard } from "@/components/mybizaclia/PublicarAnuncioWizard";
import { AnuncioEditor } from "@/components/admin/AnuncioEditor";
import {
  SOLICITUD_ESTADO_LABEL,
} from "@/lib/solicitudes.functions";

import { buildSeo } from "@/lib/seo";

const searchSchema = z.object({
  tab: z.enum(["solicitudes", "demandas", "agenda", "anuncios", "vip", "cuenta", "soporte"]).optional(),
  wizard: z.boolean().optional(),
});

export const Route = createFileRoute("/mybizaclia/")({
  validateSearch: searchSchema,
  head: () =>
    buildSeo({
      title: "Mi cuenta · Bizaclia",
      description:
        "Gestiona tus solicitudes, agenda, anuncios y datos en Bizaclia.",
      path: "/mybizaclia",
      noindex: true,
    }),
  component: MyBizacliaPage,
});


function MyBizacliaPage() {
  const { tab, wizard } = Route.useSearch();
  const navigate = useNavigate({ from: "/mybizaclia" });
  const active = tab ?? "solicitudes";
  const [sessionChecked, setSessionChecked] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const authed = !!userId;
  const [authOpen, setAuthOpen] = useState(false);
  const [tabResetKey, setTabResetKey] = useState(0);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setUserId(data.session?.user.id ?? null);
      setSessionChecked(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setUserId(session?.user.id ?? null);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const resetAnuncios = () => {
      if (active === "anuncios") {
        setTabResetKey((k) => k + 1);
      }
    };

    window.addEventListener("bizaclia:reset-mis-anuncios", resetAnuncios);
    return () => window.removeEventListener("bizaclia:reset-mis-anuncios", resetAnuncios);
  }, [active]);

  const fnResumen = useServerFn(getMyBizacliaResumen);
  const resumen = useQuery({
    queryKey: ["mybizaclia", "resumen", userId],
    queryFn: () => fnResumen(),
    enabled: authed,
  });

  if (!sessionChecked) {
    return <div className="container mx-auto max-w-5xl p-8 text-muted-foreground">Cargando…</div>;
  }

  if (!authed) {
    return (
      <div className="container mx-auto max-w-md p-8 min-h-[800px]">
        <Card>
          <CardHeader>
            <CardTitle>Accede a My Bizaclia</CardTitle>
            <CardDescription>Inicia sesión para ver tus solicitudes, agenda y anuncios.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full" onClick={() => setAuthOpen(true)}>Iniciar sesión</Button>
          </CardContent>
        </Card>
        <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
      </div>
    );
  }

  const setTab = (next: string) => {
    if (next === active) {
      setTabResetKey((k) => k + 1);
    }
    navigate({ search: { tab: next as z.infer<typeof searchSchema>["tab"], wizard: undefined }, replace: true });
  };


  const counts = resumen.data?.counts ?? { solicitudes: 0, actividades: 0, anuncios: 0, anuncios_vip: 0, demandas: 0 };

  return (
    <div className="container mx-auto max-w-6xl p-4 sm:p-6 lg:p-8 space-y-6 min-h-[800px]">
      <header className="space-y-1">
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">My Bizaclia</h1>
        <p className="text-sm text-muted-foreground">
          Tu espacio personal: solicitudes, agenda, anuncios y datos de cuenta.
        </p>
      </header>

      <Tabs value={active} onValueChange={setTab} className="w-full">
        <TabsList className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 w-full h-auto gap-2 p-2">
          <TabsTrigger value="solicitudes" className="gap-2 py-3 text-sm sm:text-base">
            <MessageSquare className="h-5 w-5" /> Solicitudes
            {counts.solicitudes > 0 && <Badge variant="secondary">{counts.solicitudes}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="demandas" className="gap-2 py-3 text-sm sm:text-base">
            <Target className="h-5 w-5" /> Demandas
            {counts.demandas > 0 && <Badge variant="secondary">{counts.demandas}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="agenda" className="gap-2 py-3 text-sm sm:text-base">
            <Calendar className="h-5 w-5" /> Agenda
            {counts.actividades > 0 && <Badge variant="secondary">{counts.actividades}</Badge>}
          </TabsTrigger>
          <TabsTrigger
            value="anuncios"
            className="gap-2 py-3 text-sm sm:text-base"
            onClick={() => {
              if (active === "anuncios") setTabResetKey((k) => k + 1);
            }}
          >
            <Megaphone className="h-5 w-5" /> Anuncios
            {counts.anuncios > 0 && <Badge variant="secondary">{counts.anuncios}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="vip" className="gap-2 py-3 text-sm sm:text-base" onClick={() => { if (active === "vip") setTabResetKey((k) => k + 1); }}>
            <Crown className="h-5 w-5" /> Off-market
            {counts.anuncios_vip > 0 && <Badge variant="secondary">{counts.anuncios_vip}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="cuenta" className="gap-2 py-3 text-sm sm:text-base">
            <UserCircle className="h-5 w-5" /> Mi cuenta
          </TabsTrigger>
          <TabsTrigger value="soporte" className="gap-2 py-3 text-sm sm:text-base">
            <LifeBuoy className="h-5 w-5" /> Atención
          </TabsTrigger>
        </TabsList>

        <TabsContent value="solicitudes" className="mt-6">
          <SolicitudesTab />
        </TabsContent>
        <TabsContent value="demandas" className="mt-6">
          <DemandasTab />
        </TabsContent>
        <TabsContent value="agenda" className="mt-6">
          <AgendaTab />
        </TabsContent>
        <TabsContent value="anuncios" className="mt-6">
          <AnunciosTab key={`${userId}-publico`} userId={userId!} mode="publico" openWizard={active === "anuncios" && wizard === true} resetKey={tabResetKey} />
        </TabsContent>
        <TabsContent value="vip" className="mt-6">
          <AnunciosTab key={`${userId}-off_market`} userId={userId!} mode="off_market" openWizard={active === "vip" && wizard === true} resetKey={tabResetKey} />
        </TabsContent>
        <TabsContent value="cuenta" className="mt-6">
          <CuentaTab contacto={resumen.data?.contacto ?? null} />
        </TabsContent>
        <TabsContent value="soporte" className="mt-6">
          <SoporteTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <Card>
      <CardContent className="py-10 text-center text-muted-foreground">
        <p className="font-medium text-foreground">{title}</p>
        {hint && <p className="text-sm mt-1">{hint}</p>}
      </CardContent>
    </Card>
  );
}

function SolicitudesTab() {
  const fn = useServerFn(listMisSolicitudes);
  const q = useQuery({ queryKey: ["mybizaclia", "solicitudes"], queryFn: () => fn() });

  if (q.isLoading) return <p className="text-sm text-muted-foreground">Cargando…</p>;
  const rows = q.data ?? [];
  if (!rows.length) {
    return (
      <EmptyState
        title="Aún no has creado solicitudes"
        hint="Cuando envíes una solicitud sobre un local aparecerá aquí."
      />
    );
  }
  return (
    <div className="space-y-3">
      {rows.map((s: any) => {
        const a = s.anuncio;
        const titulo = a?.alias ?? a?.referencia ?? "Solicitud";
        return (
          <Link
            key={s.id}
            to="/mybizaclia/solicitudes/$id"
            params={{ id: s.id }}
            className="block transition-colors"
          >
            <Card className="hover:border-primary/40">
              <CardContent className="p-4 flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <div className="space-y-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium truncate">{titulo}</p>
                    <Badge variant="outline">{SOLICITUD_ESTADO_LABEL[s.estado as keyof typeof SOLICITUD_ESTADO_LABEL] ?? s.estado}</Badge>
                  </div>
                  {s.solicitud && (
                    <p className="text-sm text-muted-foreground line-clamp-3 whitespace-pre-wrap">{s.solicitud}</p>
                  )}
                </div>
                <div className="text-xs text-muted-foreground sm:text-right shrink-0">
                  {new Date(s.created_at).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" })}
                </div>
              </CardContent>
            </Card>
          </Link>
        );
      })}
    </div>
  );
}

const DEMANDA_ESTADO_LABEL: Record<string, string> = {
  incompleta: "Incompleta",
  iniciada: "Activa",
  activa: "Activa",
  pausada: "Pausada",
  completada: "Completada",
  cancelada: "Cancelada",
  archivada: "Archivada",
};

function DemandasTab() {
  const fn = useServerFn(listMisDemandas);
  const q = useQuery({ queryKey: ["mybizaclia", "demandas"], queryFn: () => fn() });

  if (q.isLoading) return <p className="text-sm text-muted-foreground">Cargando…</p>;
  const rows = q.data ?? [];
  if (!rows.length) {
    return (
      <EmptyState
        title="Aún no has creado demandas"
        hint="Usa el Conector para crear una demanda y encontrar tu local ideal."
      />
    );
  }
  return (
    <div className="space-y-3">
      {rows.map((d: any) => {
        const titulo = d.referencia ? `Demanda ${d.referencia}` : "Mi demanda";
        return (
          <Link
            key={d.id}
            to="/conector/{-$id}"
            params={{ id: d.id }}
            search={{} as never}
            className="block transition-colors"
          >
            <Card className="hover:border-primary/40">
              <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="space-y-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium truncate">{titulo}</p>
                    <Badge variant="outline">{DEMANDA_ESTADO_LABEL[d.estado] ?? d.estado}</Badge>
                    {typeof d.match_count === "number" && d.match_count > 0 && (
                      <Badge variant="secondary">{d.match_count} coincidencias</Badge>
                    )}
                  </div>
                </div>
                <div className="text-xs text-muted-foreground sm:text-right shrink-0">
                  {new Date(d.created_at).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" })}
                </div>
              </CardContent>
            </Card>
          </Link>
        );
      })}
    </div>
  );
}

function AgendaTab() {
  const fn = useServerFn(listMisActividades);
  const q = useQuery({ queryKey: ["mybizaclia", "actividades"], queryFn: () => fn() });

  if (q.isLoading) return <p className="text-sm text-muted-foreground">Cargando…</p>;
  const rows = q.data ?? [];
  if (!rows.length) {
    return <EmptyState title="Tu agenda está vacía" hint="Aparecerán aquí las actividades donde participes." />;
  }
  return (
    <div className="space-y-3">
      {rows.map((a: any) => (
        <Card key={a.id}>
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <p className="font-medium">{a.titulo}</p>
                <Badge variant="outline">{a.tipo}</Badge>
                <Badge variant="secondary">{a.estado}</Badge>
              </div>
              {a.notas && <p className="text-sm text-muted-foreground line-clamp-2">{a.notas}</p>}
            </div>
            <div className="text-xs text-muted-foreground sm:text-right">
              {a.inicio_at
                ? new Date(a.inicio_at).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "Sin fecha"}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function AnunciosTab({ userId, mode, openWizard, resetKey }: { userId: string; mode: PublicationMode; openWizard?: boolean; resetKey?: number }) {
  const { isVip, loading: vipLoading } = useIsVip();
  const fn = useServerFn(listMisAnuncios);
  const q = useQuery({ queryKey: ["mybizaclia", "anuncios", userId, mode], queryFn: () => fn({ data: { visibilidad: mode } }) });
  const qc = useQueryClient();
  const [wizardOpen, setWizardOpen] = useState(openWizard ?? false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const previousReset = useRef(resetKey);
  useEffect(() => { setWizardOpen(openWizard ?? false); }, [openWizard]);
  useEffect(() => {
    if (previousReset.current === resetKey) return;
    previousReset.current = resetKey;
    setWizardOpen(false);
    setEditingId(null);
  }, [resetKey]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["mybizaclia", "anuncios"] });
    qc.invalidateQueries({ queryKey: ["mybizaclia", "resumen"] });
  };

  if (wizardOpen && mode === "off_market" && vipLoading) return <p>Cargando tu acceso VIP…</p>;
  if (wizardOpen && mode === "off_market" && !isVip) return (
    <Card><CardHeader><CardTitle>Activa tu acceso VIP</CardTitle><CardDescription>Accede con tu cuenta para publicar activos off-market.</CardDescription></CardHeader><CardContent className="flex gap-3"><Button asChild><Link to="/invitacion-vip">Activar mi acceso VIP</Link></Button><Button variant="outline" onClick={() => setWizardOpen(false)}>Volver</Button></CardContent></Card>
  );
  if (wizardOpen) {
    return (
      <PublicarAnuncioWizard
        mode={mode}
        onClose={() => {
          setWizardOpen(false);
          invalidate();
        }}
      />
    );
  }

  if (editingId) {
    return (
      <AnuncioEditor
        id={editingId}
        onClose={() => {
          setEditingId(null);
          invalidate();
        }}
        onSaved={invalidate}
        showSolicitudes={false}
        publicationMode={mode}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div><h2 className="text-xl font-semibold">{mode === "off_market" ? "Mis anuncios off-market" : "Mis anuncios públicos"}</h2><p className="text-sm text-muted-foreground">{mode === "off_market" ? "Gestiona los activos que compartes en el área VIP." : "Gestiona tus anuncios del portal público."}</p></div>
        <Button onClick={() => setWizardOpen(true)}>
          <Plus className="h-4 w-4 mr-2" /> {mode === "off_market" ? "Publicar activo off-market" : "Publicar nuevo anuncio"}
        </Button>
      </div>
      {q.isLoading ? (
        <p className="text-sm text-muted-foreground">Cargando…</p>
      ) : q.isError ? <p role="alert">No se pudieron cargar tus anuncios. <Button variant="link" onClick={() => q.refetch()}>Reintentar</Button></p> : (q.data ?? []).length === 0 ? (
        <EmptyState title={mode === "off_market" ? "Aún no tienes anuncios off-market" : "Aún no tienes anuncios públicos"} hint="Publica tu primer anuncio con el botón de arriba." />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {(q.data ?? []).map((a: any) => (
            <Card key={a.id}>
              <CardContent className="p-4 space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium truncate">{a.alias ?? a.referencia ?? a.id.slice(0, 8)}</p>
                  <Badge variant="outline">{a.estado}</Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  Creado {new Date(a.created_at).toLocaleDateString("es-ES")}
                </p>
                <div className="pt-2 flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setEditingId(a.id)}>
                    Editar
                  </Button>
                  <Button asChild variant="ghost" size="sm">
                    <Link to={a.visibilidad === "off_market" ? "/local-vip/$id" : "/local/$id"} params={{ id: a.id }}>Ver anuncio</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}


type CuentaContacto = {
  nombre: string | null;
  apellidos: string | null;
  email: string | null;
  telefono: string | null;
  bio?: string | null;
  foto_url?: string | null;
  notif_enabled?: boolean | null;
  notif_solicitudes?: boolean | null;
  notif_coincidencias?: boolean | null;
  notif_novedades?: boolean | null;
  notif_canal_email?: boolean | null;
  notif_canal_whatsapp?: boolean | null;
} | null;

function CuentaTab({ contacto }: { contacto: CuentaContacto }) {
  const qc = useQueryClient();
  const fn = useServerFn(updateMiCuenta);
  const [nombre, setNombre] = useState(contacto?.nombre ?? "");
  const [apellidos, setApellidos] = useState(contacto?.apellidos ?? "");
  const [email, setEmail] = useState(contacto?.email ?? "");
  const [telefono, setTelefono] = useState(contacto?.telefono ?? "");
  const [bio, setBio] = useState(contacto?.bio ?? "");
  const [fotoUrl, setFotoUrl] = useState<string | null>(contacto?.foto_url ?? null);
  const [emailChangedMsg, setEmailChangedMsg] = useState<string | null>(null);

  const [notifEnabled, setNotifEnabled] = useState(contacto?.notif_enabled ?? true);
  const [notifSolicitudes, setNotifSolicitudes] = useState(contacto?.notif_solicitudes ?? true);
  const [notifCoincidencias, setNotifCoincidencias] = useState(contacto?.notif_coincidencias ?? true);
  const [notifNovedades, setNotifNovedades] = useState(contacto?.notif_novedades ?? false);
  const [notifEmail, setNotifEmail] = useState(contacto?.notif_canal_email ?? true);
  const [notifWhatsapp, setNotifWhatsapp] = useState(contacto?.notif_canal_whatsapp ?? false);

  useEffect(() => {
    setNombre(contacto?.nombre ?? "");
    setApellidos(contacto?.apellidos ?? "");
    setEmail(contacto?.email ?? "");
    setTelefono(contacto?.telefono ?? "");
    setBio(contacto?.bio ?? "");
    setFotoUrl(contacto?.foto_url ?? null);
    setNotifEnabled(contacto?.notif_enabled ?? true);
    setNotifSolicitudes(contacto?.notif_solicitudes ?? true);
    setNotifCoincidencias(contacto?.notif_coincidencias ?? true);
    setNotifNovedades(contacto?.notif_novedades ?? false);
    setNotifEmail(contacto?.notif_canal_email ?? true);
    setNotifWhatsapp(contacto?.notif_canal_whatsapp ?? false);
    setEmailChangedMsg(null);
  }, [contacto]);

  const m = useMutation({
    mutationFn: (vars: {
      nombre: string;
      apellidos: string;
      email: string;
      telefono: string;
      bio: string;
      foto_url: string | null;
      notif_enabled: boolean;
      notif_solicitudes: boolean;
      notif_coincidencias: boolean;
      notif_novedades: boolean;
      notif_canal_email: boolean;
      notif_canal_whatsapp: boolean;
    }) => fn({ data: vars }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["mybizaclia", "resumen"] });
      if (res?.emailChanged) {
        setEmailChangedMsg("Hemos enviado un enlace de confirmación a tu nuevo correo. Hasta que lo confirmes, sigues usando tu email actual para iniciar sesión.");
      } else {
        setEmailChangedMsg(null);
      }
    },
    onError: () => setEmailChangedMsg(null),
  });

  const channelsValid = !notifEnabled || notifEmail || notifWhatsapp;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Mi cuenta</CardTitle>
        <CardDescription>Actualiza tus datos y elige cómo quieres recibir las notificaciones.</CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="space-y-6"
          onSubmit={(e) => {
            e.preventDefault();
            setEmailChangedMsg(null);
            m.mutate({
              nombre,
              apellidos,
              email,
              telefono,
              bio,
              foto_url: fotoUrl,
              notif_enabled: notifEnabled,
              notif_solicitudes: notifSolicitudes,
              notif_coincidencias: notifCoincidencias,
              notif_novedades: notifNovedades,
              notif_canal_email: notifEmail,
              notif_canal_whatsapp: notifWhatsapp,
            });
          }}
        >
          <div className="space-y-1.5">
            <Label>Foto de perfil</Label>
            <AvatarUploader
              value={fotoUrl}
              onChange={setFotoUrl}
              nombre={[nombre, apellidos].filter(Boolean).join(" ")}
              size="lg"
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="nombre">Nombre</Label>
              <Input id="nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="apellidos">Apellidos</Label>
              <Input id="apellidos" value={apellidos} onChange={(e) => setApellidos(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="telefono">Teléfono</Label>
              <Input id="telefono" value={telefono} onChange={(e) => setTelefono(e.target.value)} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="bio">Bio</Label>
              <Textarea
                id="bio"
                rows={3}
                maxLength={500}
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="Cuéntanos brevemente sobre ti."
              />
              <p className="text-xs text-muted-foreground">{bio.length}/500</p>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="email">Correo</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              <p className="text-xs text-muted-foreground">
                Si cambias tu correo, te enviaremos un enlace de confirmación. Hasta que lo aceptes seguirás usando tu email actual para iniciar sesión.
              </p>
            </div>
          </div>

          <Separator />

          <div className="space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-medium">Notificaciones</h3>
                <p className="text-sm text-muted-foreground">Activa o desactiva los avisos y elige qué tipos quieres recibir.</p>
              </div>
              <Switch checked={notifEnabled} onCheckedChange={setNotifEnabled} aria-label="Activar notificaciones" />
            </div>

            <div className={`space-y-4 ${notifEnabled ? "" : "opacity-50 pointer-events-none"}`}>
              <div>
                <p className="text-sm font-medium mb-2">Tipos de notificaciones</p>
                <div className="space-y-3 rounded-md border p-3">
                  <label className="flex items-start justify-between gap-4">
                    <span className="space-y-0.5">
                      <span className="block text-sm font-medium">Nuevas solicitudes</span>
                      <span className="block text-xs text-muted-foreground">Cuando alguien contacta sobre tus anuncios.</span>
                    </span>
                    <Switch checked={notifSolicitudes} onCheckedChange={setNotifSolicitudes} />
                  </label>
                  <label className="flex items-start justify-between gap-4">
                    <span className="space-y-0.5">
                      <span className="block text-sm font-medium">Coincidencias de demandas</span>
                      <span className="block text-xs text-muted-foreground">Cuando aparecen locales que encajan con tus búsquedas guardadas.</span>
                    </span>
                    <Switch checked={notifCoincidencias} onCheckedChange={setNotifCoincidencias} />
                  </label>
                  <label className="flex items-start justify-between gap-4">
                    <span className="space-y-0.5">
                      <span className="block text-sm font-medium">Novedades del portal</span>
                      <span className="block text-xs text-muted-foreground">Mejoras, consejos y novedades de Bizaclia. Puedes desactivarlo cuando quieras.</span>
                    </span>
                    <Switch checked={notifNovedades} onCheckedChange={setNotifNovedades} />
                  </label>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium mb-2">Canales</p>
                <div className="space-y-3 rounded-md border p-3">
                  <label className="flex items-center justify-between gap-4">
                    <span className="flex items-center gap-2 text-sm">
                      <Mail className="h-4 w-4" /> Email
                    </span>
                    <Switch checked={notifEmail} onCheckedChange={setNotifEmail} />
                  </label>
                  <label className="flex items-center justify-between gap-4">
                    <span className="flex items-center gap-2 text-sm">
                      <Phone className="h-4 w-4" /> WhatsApp
                    </span>
                    <Switch checked={notifWhatsapp} onCheckedChange={setNotifWhatsapp} />
                  </label>
                </div>
                {!channelsValid && (
                  <p className="text-xs text-destructive mt-2">Selecciona al menos un canal o desactiva las notificaciones.</p>
                )}
              </div>
            </div>
          </div>

          {emailChangedMsg && (
            <div className="rounded-md bg-blue-50 text-blue-700 p-3 text-sm border border-blue-200 dark:bg-blue-950 dark:text-blue-200 dark:border-blue-900">
              {emailChangedMsg}
            </div>
          )}
          <div className="flex items-center gap-3">
            <Button type="submit" disabled={m.isPending || !channelsValid}>
              {m.isPending ? "Guardando…" : "Guardar cambios"}
            </Button>
            {m.isSuccess && !emailChangedMsg && <span className="text-sm text-muted-foreground">Datos actualizados.</span>}
            {m.isError && <span className="text-sm text-destructive">{(m.error as Error)?.message ?? "No se pudo guardar."}</span>}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function SoporteTab() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Atención al cliente</CardTitle>
        <CardDescription>Estamos aquí para ayudarte.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <a href="tel:900433065" className="flex items-center gap-3 rounded-md border p-3 hover:bg-accent">
          <Phone className="h-4 w-4" />
          <div>
            <p className="font-medium">900 433 065</p>
            <p className="text-xs text-muted-foreground">Lunes a viernes, 9:00 – 19:00</p>
          </div>
        </a>
        <a href="mailto:info@bizaclia.com" className="flex items-center gap-3 rounded-md border p-3 hover:bg-accent">
          <Mail className="h-4 w-4" />
          <div>
            <p className="font-medium">info@bizaclia.com</p>
            <p className="text-xs text-muted-foreground">Te respondemos en menos de 24h</p>
          </div>
        </a>
      </CardContent>
    </Card>
  );
}
